import React from 'react'

import HeaderMeta from 'components/HeaderMeta'

import Content, { Theme as ContentTheme } from 'components/base/Content'
import Img from 'components/base/Img'
import Paragraph, { Theme as ParagraphTheme } from 'components/base/Paragraph'
import Link  from 'components/base/Link'
import Title from 'components/base/Title'
import SectionRule from 'components/base/SectionRule/styles'
import Faq from 'components/blocks/Faq'

import PageHero, { Theme as PageHeroTheme } from 'components/blocks/PageHero'
import AskQuestion from 'components/blocks/AskQuestion'
import InsuranceTypeIcon, { Theme as InsuranceTypeIconTheme } from 'components/base/Icon/InsuranceTypeIcon'
import StartQuoteButton from 'components/base/StartQuoteButton'

import DrCheckUp from 'assets/images/products/DrCheckUp.svg'
import HealthType from 'assets/images/products/HealthType.svg'
import HealthSherpa from 'assets/images/products/HealthSherpa.png'
import OpenEnrollment from 'assets/images/products/OpenEnrollmentGraphic.svg'

const ProductsHealthInsurance: React.FC = (): React.ReactElement => (
  <div>
    <HeaderMeta
      title='Health Insurance | Insurance Lounge'
      description='Choosing health insurance has never been so easy. At Insurance Lounge, our experts are ready to assist you in finding the perfect policy for your needs.'
      ogImg='/images/og-images/Health.webp'
    />
    <PageHero
      theme={PageHeroTheme.health}
      cta={true}
      ctaShowExternalIcon={true}
      ctaTitle='Get a health insurance quote now.'
      ctaUrl='/quote/new/health'
      trackingInfo={{ page_category: 'product_page_health' }}
    >
      A Guide to Health Insurance Plans
    </PageHero>
    <Content short={true} margined={true} center={true}>
      <InsuranceTypeIcon theme={InsuranceTypeIconTheme.health} colored={true} hasMargin={true} />
      <Paragraph theme={ParagraphTheme.introLarge}>
        It can sometimes be challenging to find the right health insurance plan for you and your family.
        There are so many different plans, with so many different benefits, that it can get overwhelming.
        And of course, there’s always the cost to consider. But if you’re looking for a way to get affordable
        coverage without sacrificing quality care or choice in doctors, then contacting
        Insurance Lounge is your next move.
      </Paragraph>
    </Content>
    <Content theme={ContentTheme.gray}>
      <Content theme={ContentTheme.paddedBelow} center={true} short={true}>
        <Img
          src={OpenEnrollment}
          src2x={undefined}
          style={{
            height: 160,
            width: 'auto',
          }}
          alt='Open Enrollment'
        />
      </Content>
      <Content center={true} short={true}>
        <Title level={2}>Open Enrollment is Here</Title>
        <Paragraph>
          The open enrollment period for people to sign up or make changes to their individual health plans is November 1st thru January 15th 2023.
        </Paragraph>
        <Paragraph>
          <StartQuoteButton
            trackingInfo={{
              button_position: 'middle',
              page_category: 'product_page_health',
            }}
            showExternalIcon={true}
            to='/quote/new/health'
          >
            See plans and prices
          </StartQuoteButton>
        </Paragraph>
      </Content>
    </Content>
    <Content theme={ContentTheme.yellow}>
      <Content theme={ContentTheme.paddedBelow} center={true} short={true}>
        <Img
          src={DrCheckUp}
          src2x={undefined}
          style={{
            height: 260,
            width: 'auto',
          }}
          alt='Dr Checkup'
        />
      </Content>
      <Content center={true} short={true}>
        <Title level={2}>How to Choose the Right Plan</Title>
        <Paragraph>
          The first step is asking the opinion of someone you trust before open enrollment, such as your physician or another healthcare professional.
          They’ll be able to tell you what they think about different plans in terms of quality of coverage, level of expertise available nearby,
          affordability, and other factors that you might not think about.
        </Paragraph>
        <Title level={2}>Health Insurance for You</Title>
        <Paragraph>
          If you’re looking for health insurance for just you, it’s important to know your options during your special enrollment period.
          If you’re single, then the best choice for you will probably be an individual health insurance plan tailored specifically
          to meet your needs and budget. Keep in mind that these plans are not usually as comprehensive or affordable as family health
          insurance, but they may be the best choice for you if your situation allows it.
        </Paragraph>
        <Title level={2}>Health Insurance for Your Family</Title>
        <Paragraph>
          Even if no one in your family has any pre-existing medical conditions or problems, everyone should still have some
          sort of health insurance. Family plans tend to be more comprehensive and expensive than individual ones, but
          they also give you a lot more options when it comes time for your family’s care.

        </Paragraph>
        <Title level={2}>Factors That Impact Your Health Insurance Premiums</Title>
        <Paragraph>
          Many people don’t realize that certain factors can impact your health insurance premiums.
          These include age, income level, smoking habits, and family history. For example, someone with cancer
          should expect to pay more for their plan than another person in the same demographic group. And if you have
          a pre-existing condition or medical problem, you can expect to face significantly higher premiums or even get denied coverage.
        </Paragraph>
      </Content>
    </Content>
    <Content margined={true} center={true} short={true}>
      <Content theme={ContentTheme.paddedBelow} center={true} short={true}>
        <Img
          src={HealthType}
          src2x={undefined}
          style={{
            height: 160,
            width: 'auto',
          }}
          alt='Health Insurance'
        />
      </Content>
      <Title level={2}>Common Types of Health Insurance Coverage</Title>
      <Title level={3}>PPO</Title>
      <Paragraph>
        Preferred Provider Organization (PPO) health insurance plans give you access to a wide variety of doctors and hospitals.
        This type of plan typically covers 60 - 80% of your costs, with the rest being up to you.
      </Paragraph>
      <Title level={3}>HMO</Title>
      <Paragraph>
        Health Maintenance Organization (HMO) health insurance plans only give you access to a certain network of doctors and hospitals.
        This type of plan typically covers 80 - 90% of your costs, with the rest being up to you.
      </Paragraph>
      <Title level={3}>HSA</Title>
      <Paragraph>
        Health Savings Account (HSA) health insurance plans are a type of high-deductible plan paired with a special savings account.
        This type of plan typically covers 60 - 80% of your costs, but the money in your HSA can be used to offset uncovered expenses like copays.
      </Paragraph>
    </Content>
    <Content theme={ContentTheme.yellow}>
      <Content theme={ContentTheme.paddedBelow} center={true} short={true}>
        <Img
          src={HealthSherpa}
          src2x={undefined}
          style={{
            height: 60,
            width: 'auto',
          }}
          alt='Health Sherpa'
        />
      </Content>
      <Content center={true} short={true}>
        <Title level={2} margin={true}>Please click below to visit our quoting site on HealthSherpa.</Title>
        <Paragraph>
          <StartQuoteButton
            trackingInfo={{
              button_position: 'bottom',
              page_category: 'product_page_health',
            }}
            showExternalIcon={true}
            to='/quote/new/health'
          >
            Start Your Quote
          </StartQuoteButton>
        </Paragraph>
      </Content>
    </Content>
    <Content theme={ContentTheme.gray}>
      <Content center={true} short={true}>
        <Title level={2}>Continuation of Health Insurance: COBRA</Title>
        <Paragraph>
          COBRA is an acronym for the Consolidated Omnibus Budget Reconciliation Act, a federal law requiring employers
          to let their employees (and dependents) take over their health insurance coverage if they leave or get fired.
          You can get COBRA if you are already enrolled in your employer’s plan, have left the company for any reason,
          were fired or laid off from that job (but only within 60 days of termination), are on strike, or retired.
          Find more information on COBRA by speaking with your insurance provider or your HR department at work.
        </Paragraph>
      </Content>
    </Content>
    <Content theme={ContentTheme.yellow}>
      <Content short={true} center={true}>
        <Title level={2}>Frequently Asked Questions</Title>
        <Faq
          items={[
            {
              question: 'How much does health insurance cost?',
              answer: (
                <>
                  <Paragraph>
                    Your costs for health insurance can vary based on the scope of coverage, the type of plan you have,
                    and your deductibles. Copays and coinsurance can also add to the cost, so it’s important to
                    consider what you’ll pay before enrolling in a health care plan.
                  </Paragraph>
                </>
              ),
            },
            {
              question: 'Why is health insurance important?',
              answer: (
                <>
                  <Paragraph>
                    It’s essential to have coverage when your health takes a turn for the worst, as that can result
                    in tens or hundreds of thousands of dollars worth of debt if not covered by an adequate plan.
                  </Paragraph>
                </>
              ),
            },
            {
              question: 'What are some tips for choosing the best health insurance?',
              answer: (
                <>
                  <Paragraph>
                    When choosing the best health insurance plan, you should consider your level of coverage (the scope),
                    what type of deductible and copays it has, whether or not it includes any pre-existing conditions,
                    how much money will go to premiums vs. out-of-pocket expenses, etc.
                  </Paragraph>
                </>
              ),
            },
            {
              question: 'I can’t afford to buy health insurance. What should I do?',
              answer: (
                <>
                  <Paragraph>
                    If you’re having trouble affording health insurance, then you should consider looking
                    into <Link to='/products/medicare-insurance'>Medicare</Link>, Medicaid, or CHIP coverage.
                    You may also qualify for a subsidy if your income is below 400% of the poverty level and purchase
                    insurance through one of the exchanges available in your state.
                  </Paragraph>
                </>
              ),
            },
          ]}
        />
      </Content>
    </Content>
    <Content margined={true} center={true} short={true}>
      <Title level={2}>Work With a Reputable Insurance Agency</Title>
      <Paragraph>
        If you need <Link to='/products/health-insurance'>health insurance</Link>, contact the experts at Insurance Lounge.
        For more information, fill out our <Link to='/quote/new'>quote and buy form</Link> or give us a call at (541) 479-4975.
      </Paragraph>
    </Content>
    <SectionRule />
    <AskQuestion title='We’re Here, When You Need Us'>
      Reach out and an Insurance Concierge<sup>®</sup> will get in touch!
    </AskQuestion>
  </div>
)

export default ProductsHealthInsurance
